import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

interface AsyncRequestHelpers<TData> {
  isCurrentRequest: () => boolean;
  setData: Dispatch<SetStateAction<TData>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

interface UseAsyncDataRunOptions<TData, TResult> {
  showLoadingState?: boolean;
  clearError?: boolean;
  onSuccess?: (
    result: TResult,
    helpers: AsyncRequestHelpers<TData>,
  ) => Promise<void> | void;
  onError?: (
    error: unknown,
    helpers: AsyncRequestHelpers<TData>,
  ) => Promise<void> | void;
}

interface UseAsyncDataOptions<TData> {
  initialData: TData;
  initialLoading?: boolean;
}

export function useAsyncData<TData>({
  initialData,
  initialLoading = false,
}: UseAsyncDataOptions<TData>) {
  const [data, setData] = useState<TData>(initialData);
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState<string | null>(null);
  const latestRequestRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      latestRequestRef.current += 1;
    };
  }, []);

  const invalidate = useCallback(() => {
    latestRequestRef.current += 1;
  }, []);

  const run = useCallback(
    async <TResult>(
      load: () => Promise<TResult>,
      {
        showLoadingState = true,
        clearError = true,
        onSuccess,
        onError,
      }: UseAsyncDataRunOptions<TData, TResult> = {},
    ): Promise<TResult | undefined> => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      const isCurrentRequest = () =>
        isMountedRef.current && latestRequestRef.current === requestId;

      const helpers: AsyncRequestHelpers<TData> = {
        isCurrentRequest,
        setData,
        setError,
        setIsLoading,
      };

      if (clearError && isCurrentRequest()) {
        setError(null);
      }

      if (showLoadingState && isCurrentRequest()) {
        setIsLoading(true);
      }

      try {
        const result = await load();
        if (onSuccess && isCurrentRequest()) {
          await onSuccess(result, helpers);
        }
        return result;
      } catch (loadError) {
        if (onError && isCurrentRequest()) {
          await onError(loadError, helpers);
        }
        return undefined;
      } finally {
        if (showLoadingState && isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  return {
    data,
    setData,
    isLoading,
    setIsLoading,
    error,
    setError,
    invalidate,
    run,
  };
}
